import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  LoadingSpinner,
  useDialogExitWarning,
  VisuallyHidden,
} from '@ais-ui/ui-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ParsedReportParams, ReportTemplate } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { createGeometryFromGeoJson } from '@ais-ui/utils';

import { FilterType, reportTypesObject, ReportType, ShapeType } from '../../reportsTypes';
import { MutateReportProvider, useMutateReportContext } from '../CreateReportTemplate/CreateReportContext';
import { useReportFormResources } from '../CreateReportTemplate/hooks/useReportFormResources';
import { findFilterTypeFromReport } from '../utils/findFilterTypeFromReport';
import { findShapeTypeFromReport } from '../utils/findShapeTypeFromKey';
import { useCloneReportForm, useCloneSubmit } from './hooks';
import { CloneReportForm } from './CloneReportForm';
import { GeoJsonDictionary } from '../../utils';

interface CloneReportProps {
  setShowPopover: (showPopover: boolean) => void;
  report: ParsedReportParams | undefined;
  reportTemplate: ReportTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoadingReport?: boolean;
}

interface CloneReportContentProps {
  report: ParsedReportParams | undefined;
  reportType: ReportType;
  setShowPopover: (showPopover: boolean) => void;
  localShowDialog: boolean;
  setLocalShowDialog: (show: boolean) => void;
  databaseRange: { database: { begin: Date; end: Date } } | undefined;
  isLoadingDatabaseRange: boolean;
  areResourcesLoading: boolean;
  isLoadingShareGroups: boolean;
  shapes: any;
  filters: any;
  ports: any;
  initialDynamicShape: GeoJson | undefined;
  initialIsDynamic: boolean;
  instantShape: boolean;
  instantShapeData: Record<string, GeoJson>;
}

/**
 * Inner content component that has access to MutateReportContext.
 * This enables proper sync between local dialog state and context state for drawing functionality.
 */
const CloneReportContent = ({
  report,
  reportType,
  setShowPopover,
  localShowDialog,
  setLocalShowDialog,
  databaseRange,
  isLoadingDatabaseRange,
  areResourcesLoading,
  isLoadingShareGroups,
  shapes,
  filters,
  ports,
  initialDynamicShape,
  initialIsDynamic,
  instantShape,
  instantShapeData,
}: CloneReportContentProps) => {
  const { messages } = useLocalization();

  const [filterType, setFilterType] = useState<FilterType>(
    findFilterTypeFromReport(report, reportType),
  );
  const [shapeType, setShapeType] = useState<ShapeType>(
    findShapeTypeFromReport(report),
  );

  // Setup form with schema and initial values
  const { form, isLoading } = useCloneReportForm({
    report,
    reportType,
    shapeType,
    filterType,
    databaseRange,
    isLoadingDatabaseRange,
    areResourcesLoading,
    isLoadingShareGroups,
    instantShape,
    instantShapeData,
    initialIsDynamic,
    initialDynamicShape,
  });

  // Handle form submission
  const { onClone, isSubmitting } = useCloneSubmit({
    report,
    reportType,
    shapeType,
    filterType,
    shapes,
    filters,
    ports,
    instantShape,
    instantShapeData,
    initialIsDynamic,
    dynamicShape: initialDynamicShape,
    setLocalShowDialog,
    setShowPopover,
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="h-fit pb-10">
        <h3 className="text-lg font-medium">{messages.reportsCloneReportTitle}</h3>
        <p className="text-sm text-muted-foreground">
          {messages.reportsCloneReportSubtitle}
        </p>
      </div>
      <CloneReportForm
        form={form}
        reportType={reportType}
        filterType={filterType}
        setFilterType={setFilterType}
        shapeType={shapeType}
        setShapeType={setShapeType}
        databaseRange={databaseRange}
        onSubmit={onClone}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

/**
 * Detects if the cloned report has an instant shape ("Shape drawn in MDM").
 */
function useDetectInstantShape(report: ParsedReportParams | undefined) {
  const [dynamicShape, setDynamicShape] = useState<GeoJson | undefined>();
  const [isDynamic, setIsDynamic] = useState(false);

  useEffect(() => {
    const shapeRaw = report?.params['GAC']?.shape;
    if (!shapeRaw) return;

    let shape;
    try {
      shape = typeof shapeRaw === 'string' ? JSON.parse(shapeRaw) : shapeRaw;
    } catch (e) {
      console.warn('Invalid shape JSON:', e);
      return;
    }

    const name = shape?.properties?.name;
    if (name === 'Shape drawn in MDM') {
      setIsDynamic(true);
      setDynamicShape(shape);
    }
  }, [report?.params]);

  return { dynamicShape, isDynamic };
}

interface CloneReportDialogWrapperProps {
  externalOpen: boolean;
  onExternalOpenChange: (open: boolean) => void;
  handleExitConfirm: () => void;
  exitWarningTitle: string;
  exitWarningDescription: string;
  isLoading: boolean;
  messages: Record<string, string>;
  report: ParsedReportParams | undefined;
  reportType: ReportType;
  setShowPopover: (showPopover: boolean) => void;
  databaseRange: { database: { begin: Date; end: Date } } | undefined;
  isLoadingDatabaseRange: boolean;
  areResourcesLoading: boolean;
  isLoadingShareGroups: boolean;
  shapes: any;
  filters: any;
  ports: any;
  dynamicShape: GeoJson | undefined;
  isDynamic: boolean;
}

/**
 * Dialog wrapper component that uses MutateReportContext for dialog state.
 * Must be rendered inside MutateReportProvider.
 * 
 * This follows the same pattern as CreateReportTemplate - the dialog's open state
 * is controlled directly by the context, so useDrawFromDialog can close/reopen it.
 */
const CloneReportDialogWrapper = ({
  externalOpen,
  onExternalOpenChange,
  handleExitConfirm,
  exitWarningTitle,
  exitWarningDescription,
  isLoading,
  messages,
  report,
  reportType,
  setShowPopover,
  databaseRange,
  isLoadingDatabaseRange,
  areResourcesLoading,
  isLoadingShareGroups,
  shapes,
  filters,
  ports,
  dynamicShape,
  isDynamic,
}: CloneReportDialogWrapperProps) => {
  // Use context's showDialog directly - same pattern as CreateReportTemplate
  const {
    showDialog: contextShowDialog,
    setShowDialog: setContextShowDialog,
    instantShape,
    setInstantShape,
    instantShapeData,
    setInstantShapeData,
  } = useMutateReportContext();

  // Track the previous context value to detect external changes vs internal changes
  const prevContextRef = useRef(contextShowDialog);
  const prevExternalRef = useRef(externalOpen);

  useEffect(() => {
    const contextChanged = contextShowDialog !== prevContextRef.current;
    const externalChanged = externalOpen !== prevExternalRef.current;

    // If external changed (user action like clicking clone button or closing popover)
    if (externalChanged && !contextChanged) {
      setContextShowDialog(externalOpen);
    }
    // If context changed (internal action like useDrawFromDialog)
    else if (contextChanged && !externalChanged) {
      onExternalOpenChange(contextShowDialog);
    }
    // If both changed or neither changed on first render, prefer external
    else if (externalOpen !== contextShowDialog) {
      if (externalOpen && !contextShowDialog) {
        setContextShowDialog(true);
      }
    }

    prevContextRef.current = contextShowDialog;
    prevExternalRef.current = externalOpen;
  }, [contextShowDialog, externalOpen, setContextShowDialog, onExternalOpenChange]);

  // Initialize instant shape mode from cloned report if it had an instant shape
  useEffect(() => {
    if (isDynamic && dynamicShape) {
      setInstantShape(true);
      const shapeFieldName = reportType.shape?.shape?.[0]?.name || 'shape';
      // Convert GeoJson to Geometry to match the expected schema format
      const geometry = createGeometryFromGeoJson(dynamicShape);
      // Note: GeoJsonDictionary type is misleading - context actually stores Geometry objects
      setInstantShapeData({ [shapeFieldName]: geometry } as unknown as GeoJsonDictionary);
    }
  }, [isDynamic, dynamicShape, setInstantShape, setInstantShapeData, reportType.shape]);

  const onExitConfirm = useCallback(() => {
    setContextShowDialog(false);
    onExternalOpenChange(false);
    handleExitConfirm();
  }, [setContextShowDialog, onExternalOpenChange, handleExitConfirm]);

  const { handleClose, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: onExitConfirm,
    title: exitWarningTitle,
    description: exitWarningDescription,
  });

  const handleOpenChange = useCallback((open: boolean) => {
    setContextShowDialog(open);
    onExternalOpenChange(open);
  }, [setContextShowDialog, onExternalOpenChange]);

  return (
    <>
      <Dialog open={contextShowDialog} onOpenChange={handleOpenChange}>
        <DialogContent
          className="p-8 md:p-12 w-full !max-w-lg md:!max-w-xl lg:!max-w-3xl h-auto min-h-[360px] md:min-h-[640px] lg:min-h-[840px]"
          onClose={handleClose}
        >
          <VisuallyHidden>
            <DialogTitle>{messages.reportsCloneReportTitle}</DialogTitle>
            <DialogDescription>{messages.reportsCloneReportSubtitle}</DialogDescription>
          </VisuallyHidden>
          {isLoading && <LoadingSpinner />}
          {!isLoading && (
            <CloneReportContent
              report={report}
              reportType={reportType}
              setShowPopover={setShowPopover}
              localShowDialog={contextShowDialog}
              setLocalShowDialog={setContextShowDialog}
              databaseRange={databaseRange}
              isLoadingDatabaseRange={isLoadingDatabaseRange}
              areResourcesLoading={areResourcesLoading}
              isLoadingShareGroups={isLoadingShareGroups}
              shapes={shapes}
              filters={filters}
              ports={ports}
              initialDynamicShape={dynamicShape}
              initialIsDynamic={isDynamic}
              instantShape={instantShape}
              instantShapeData={instantShapeData}
            />
          )}
        </DialogContent>
      </Dialog>
      <ExitWarningDialog />
    </>
  );
};

/**
 * Main CloneReport component that provides the dialog wrapper and context.
 */
export const CloneReport = ({
  setShowPopover,
  report,
  reportTemplate,
  open,
  onOpenChange,
  isLoadingReport = false,
}: CloneReportProps) => {
  const { messages } = useLocalization();

  const reportType = useMemo(
    () => reportTypesObject[reportTemplate.type],
    [reportTemplate.type],
  );

  const {
    shapes,
    filters,
    ports,
    databaseRange,
    isLoading: areResourcesLoading,
    isLoadingShareGroups,
    isLoadingDatabaseRange,
  } = useReportFormResources({ reportType });

  const { dynamicShape, isDynamic } = useDetectInstantShape(report);

  const isLoading = areResourcesLoading || isLoadingShareGroups || isLoadingReport;

  return (
    <MutateReportProvider
      startDatetimeThreshold={databaseRange?.database.begin}
      endDatetimeThreshold={databaseRange?.database.end}
    >
      <CloneReportDialogWrapper
        externalOpen={open}
        onExternalOpenChange={onOpenChange}
        handleExitConfirm={() => setShowPopover(false)}
        exitWarningTitle={messages.reportsCloneWarningTitle || 'Discard clone?'}
        exitWarningDescription={messages.reportsCloneWarningDescription || 'Your clone configuration will be lost. Are you sure you want to close?'}
        isLoading={isLoading}
        messages={messages}
        report={report}
        reportType={reportType}
        setShowPopover={setShowPopover}
        databaseRange={databaseRange}
        isLoadingDatabaseRange={isLoadingDatabaseRange}
        areResourcesLoading={areResourcesLoading}
        isLoadingShareGroups={isLoadingShareGroups}
        shapes={shapes}
        filters={filters}
        ports={ports}
        dynamicShape={dynamicShape}
        isDynamic={isDynamic}
      />
    </MutateReportProvider>
  );
};
